<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    offset-y
    left
    min-width="400"
    max-width="400"
    transition="slide-y-transition"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn icon v-bind="attrs" v-on="on" class="mr-2">
        <v-badge
          :content="unreadCount"
          :value="unreadCount"
          color="red"
          overlap
        >
          <v-icon>mdi-bell</v-icon>
        </v-badge>
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="d-flex justify-space-between align-center py-2 px-4">
        <span class="text-subtitle-1 font-weight-bold">
          {{ $t('notifications.title') }}
        </span>
        <v-btn
          v-if="unreadCount > 0"
          text
          x-small
          color="primary"
          class="no-uppercase"
          @click="markAllAsRead"
        >
          {{ $t('notifications.markAllAsRead') }}
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-list three-line class="pa-0 notification-list">
        <template v-if="notifications.length > 0">
          <v-list-item
            v-for="item in notifications"
            :key="item.id"
            :class="{ 'unread-item': !item.readAt }"
            @click="handleNotificationClick(item)"
          >
            <v-list-item-avatar color="primary lighten-4">
              <v-icon color="primary">
                {{ ['EXPECTED_CHECK_IN', 'CHECKED_IN'].includes(item.type) ? 'mdi-login' : 'mdi-logout' }}
              </v-icon>
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title class="text-wrap">
                {{ formatNotificationMessage(item) }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ formatDateTime(item.createdAt) }}
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action v-if="!item.readAt">
              <v-badge dot color="primary"></v-badge>
            </v-list-item-action>
          </v-list-item>
        </template>
        <v-list-item v-else>
          <v-list-item-content>
            <v-list-item-title class="text-center grey--text">
              {{ $t('notifications.noNotifications') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
      <v-divider></v-divider>

      <div class="pa-2 grey lighten-4 text-center">
        <v-btn
          text
          block
          small
          color="primary"
          class="no-uppercase"
          @click="seeAll"
        >
          {{ $t('notifications.seeAll') }}
        </v-btn>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';

export default {
  name: 'NotificationsComponent',
  data() {
    return {
      menu: false,
      updateInterval: null
    };
  },
  computed: {
    ...mapGetters({
      notifications: 'getNotifications',
      unreadCount: 'getUnreadCount'
    })
  },
  async mounted() {
    await this.fetchUnreadCount();
    // Refresh unread count every minute
    this.updateInterval = setInterval(this.fetchUnreadCount, 60000);
  },
  beforeDestroy() {
    if (this.updateInterval) clearInterval(this.updateInterval);
  },
  watch: {
    menu(val) {
      if (val) {
        this.fetchNotifications();
      }
    }
  },
  methods: {
    ...mapActions([
      'fetchMyNotifications',
      'fetchUnreadCount',
      'markNotificationsAsRead',
      'markAllNotificationsAsRead'
    ]),
    async fetchNotifications() {
      await this.fetchMyNotifications({
        skip: 0, take: 20,
        orderBy: [{ fieldName: 'createdAt', direction: 'Desc' }]
      });
    },
    async markAllAsRead() {
      await this.markAllNotificationsAsRead();
      if (this.menu) {
        await this.fetchNotifications();
      }
    },
    async handleNotificationClick(item) {
      if (!item.readAt) {
        await this.markNotificationsAsRead([item.id]);
        if (this.menu) {
          await this.fetchNotifications();
        }
      }
      // Optional: Navigate to booking or specific page
      if (item.bookingId) {
        this.$router.push(`/checkerboard?bookingId=${item.bookingId}`).catch(() => {});
        this.menu = false;
      }
    },
    seeAll() {
      this.menu = false;
      this.$router.push('/notifications').catch(() => {});
    },
    formatNotificationMessage(item) {
      const customer = item.booking?.representative?.fullName || 'N/A';
      const targetDate = item.type === 'EXPECTED_CHECK_IN' 
        ? item.booking?.fromDate 
        : item.booking?.toDate;
      
      const timeRemaining = targetDate ? moment(targetDate).fromNow(true) : 'N/A';
      
      return this.$t(`notifications.messages.${item.type}`, {
        customer,
        time: timeRemaining
      });
    },
    formatDateTime(date) {
      return moment(date).format('DD.MM.YYYY HH:mm');
    }
  }
};
</script>

<style scoped lang="scss">
.notification-list {
  max-height: 400px;
  overflow-y: auto;
}

.unread-item {
  background-color: #f5f8ff;
}

.no-uppercase {
  text-transform: none;
}
</style>
